
import { ClassCard } from "./ClassCard";
import { ClassesListLoading } from "./LoadingStates";
import { GraduationCap, Search } from "lucide-react";

interface Class {
  id: string;
  title: string;
  description: string;
  instructor: string;
  schedule: string;
  capacity: number;
  duration: number;
  enrolledCount: number;
  created_by?: string;
}

interface ClassesListProps {
  classes: Class[] | undefined;
  isLoading: boolean;
  searchQuery: string;
  isSubscribed: (classId: string) => boolean;
  handleClassAction: (classId: string) => void;
  subscribeMutation: any;
  unsubscribeMutation: any;
  activeView?: string;
  userRole?: string;
  userProfile?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
  } | null;
}

export const ClassesList = ({
  classes,
  isLoading,
  searchQuery,
  isSubscribed,
  handleClassAction,
  subscribeMutation,
  unsubscribeMutation,
  activeView = 'all',
  userRole,
  userProfile,
}: ClassesListProps) => {
  if (isLoading) {
    return <ClassesListLoading />;
  }

  const filteredClasses = classes?.filter(class_ => {
    const matchesSearch = class_.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         class_.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         class_.instructor.toLowerCase().includes(searchQuery.toLowerCase());

    if (activeView === 'subscribed') {
      // Para profesores, "Mis clases" significa clases donde soy instructor
      if (userRole === 'profesor' && userProfile) {
        const instructorName = `${userProfile.first_name} ${userProfile.last_name}`;
        return matchesSearch && class_.instructor === instructorName;
      }
      // Para usuarios normales, significa clases donde estoy suscrito
      return matchesSearch && isSubscribed(class_.id);
    }

    // Filter by created_by for professors viewing their created classes
    if (activeView === 'created' && userProfile && userRole === 'profesor') {
      return matchesSearch && class_.created_by === userProfile.id;
    }
    
    return matchesSearch;
  });

  if (filteredClasses?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 animate-fade-in">
        <div className="p-6 rounded-full bg-primary/10 mb-6">
          {searchQuery ? (
            <Search className="w-12 h-12 text-primary/60" />
          ) : (
            <GraduationCap className="w-12 h-12 text-primary/60" />
          )}
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2 text-center">
          {searchQuery 
            ? "Sin resultados"
            : activeView === 'subscribed' 
              ? "Aún no estás inscrito"
              : "No hay clases disponibles"}
        </h3>
        <p className="text-muted-foreground text-center max-w-sm">
          {searchQuery 
            ? "No se encontraron clases que coincidan con tu búsqueda. Intenta con otros términos."
            : activeView === 'subscribed' 
              ? "Explora las clases disponibles y únete a las que más te interesen."
              : "Pronto habrá nuevas clases disponibles para ti."}
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-7xl mx-auto mt-6">
      {filteredClasses?.map((class_, index) => (
        <ClassCard 
          key={class_.id} 
          class_={class_}
          isSubscribed={isSubscribed(class_.id)}
          onAction={() => handleClassAction(class_.id)}
          isLoading={subscribeMutation.isPending || unsubscribeMutation.isPending}
          userRole={userRole}
          viewMode={activeView}
          currentUserInstructorName={
            userProfile && userRole === 'profesor' 
              ? `${userProfile.first_name} ${userProfile.last_name}`
              : undefined
          }
          index={index}
        />
      ))}
    </div>
  );
};
